import { photoZoom } from './fades';
import { clipAt, clipLength, clipStarts, isEmptySlot, totalLength, voiceSpans, type VideoProject } from './project';

/*
 * Preview on the video screen: two <video> elements take turns (the next clip is loaded in the hidden one, so the
 * change between clips is quick) and an <audio> plays the music. Volumes go through Web Audio, because iPhone and
 * iPad ignore a media element's own volume. This is a preview only; the exported file is made frame by frame
 * (src/video/exporter.ts).
 *
 * v3 (เทมเพลตวิดีโอ): some parts of the timeline have no moving picture — a still picture (an <img> that zooms in
 * slowly), an empty slot (nothing; the video screen shows its name), and the last picture held after a clip that is
 * shorter than its slot. For those the time runs on the clock ('clock' mode) instead of a <video> element.
 *
 * v4 (เสียงพากย์ AI): one more <audio> plays the scene's voice from the server while its scene plays, kept within a
 * moment of the timeline; the clips' sound and the music are lowered under it by `levels` (src/video/fades.ts duckAt).
 */

export interface PlayerHooks {
  /** timeline time while playing or after a seek (seconds) */
  onTime: (t: number) => void;
  onPlayState: (playing: boolean) => void;
  /** a clip or the music cannot be shown (e.g. not on this device): Thai reason */
  onProblem: (reason: string) => void;
  /** V2 fades: sound levels at timeline time t (times each clip's / the music's own volume) */
  levels?: (t: number) => { clip: number; music: number };
  /** v4: the AI voice's level at t (start/end fades) */
  voiceLevel?: (t: number) => number;
}

/** the picture on screen, for the still behind the text editor */
export interface Picture { source: CanvasImageSource; w: number; h: number; zoom: number }

export function createPlayer(box: HTMLElement, urlOf: (key: string) => Promise<string | null>, hooks: PlayerHooks) {
  const make = () => {
    const v = document.createElement('video');
    v.playsInline = true; v.preload = 'auto'; v.muted = false;
    v.setAttribute('playsinline', '');
    v.className = 'vplayer';
    box.append(v);
    return v;
  };
  const els = [make(), make()];
  const photo = document.createElement('img');
  photo.className = 'vplayer vphoto'; photo.alt = ''; photo.hidden = true;
  box.append(photo);
  const music = new Audio();
  music.preload = 'auto';
  const voice = new Audio();          // v4: the scene's AI voice (a file on the server)
  voice.preload = 'auto';
  let voiceProblem = '';              // the voice file last reported as unplayable (said once, not every frame)
  voice.addEventListener('error', () => {
    const src = voice.getAttribute('src') ?? '';
    if (!src || src === voiceProblem) return;
    voiceProblem = src;
    hooks.onProblem('เล่นเสียงพากย์ไม่ได้ (โหลดไฟล์เสียงจากเซิร์ฟเวอร์ไม่สำเร็จ ต้องออนไลน์) ถ้ายังไม่ได้ ลองสร้างเสียงของฉากนั้นใหม่');
  });
  let front = 0;                  // index of the video element on screen
  let shownClip = -1;             // clip index on screen
  let project: VideoProject | null = null;
  let t = 0;
  let playing = false;
  let raf = 0;
  let ctx: AudioContext | null = null;
  const gains = new Map<HTMLMediaElement, GainNode>();
  let loadRun = 0;               // +1 on every seek, clip change and pause: an older one that finishes late does nothing
  let starting = false;           // play() is getting the first clip ready
  let lastGone = -1;              // the last clip whose file was not on this device
  /** 'video': time comes from the <video>; 'clock': a still picture, an empty slot or a held last picture */
  let mode: 'video' | 'clock' = 'video';
  let clockBase = 0, clockFrom = 0;

  els[1].hidden = true;

  /** which element is on screen: a video element, the still picture, or nothing (black) */
  function show(what: 'video' | 'photo' | 'none') {
    els[front].hidden = what !== 'video';
    els[1 - front].hidden = true;
    photo.hidden = what !== 'photo';
  }

  /** Web Audio for volumes; made on the first tap (browsers allow sound only after one) */
  function audio() {
    if (ctx) return;
    try {
      ctx = new AudioContext();
      for (const el of [...els, music, voice]) {
        const g = ctx.createGain();
        ctx.createMediaElementSource(el).connect(g).connect(ctx.destination);
        gains.set(el, g);
      }
    } catch { ctx = null; }   // no Web Audio: the media elements play at full volume
  }
  function setVolume(el: HTMLMediaElement, v: number) {
    const g = gains.get(el);
    if (g) g.gain.value = v; else el.muted = v === 0;
  }

  /** a stored file's address; a store that cannot be read is reported (not taken for "not on this device") */
  async function addressOf(key: string): Promise<string | null> {
    try { return await urlOf(key); }
    catch (e) { hooks.onProblem('อ่านคลิปจากที่เก็บในเครื่องไม่ได้: ' + (e instanceof Error ? e.message : String(e))); return null; }
  }

  /** put video clip k at `at` into `el`; false when its file is not here or a newer seek/play/pause took over */
  async function load(el: HTMLVideoElement, k: number, at: number, run: number): Promise<boolean> {
    const c = project!.clips[k];
    lastGone = -1;
    const url = await addressOf(c.media);
    if (!url) { lastGone = k; return false; }   // not on this device: the video screen shows that over the picture
    if (run !== loadRun) return false;
    if (el.src !== url) { el.src = url; }
    await seekEl(el, at);
    if (run !== loadRun) return false;
    setVolume(el, c.hasAudio ? c.volume : 0);
    return true;
  }

  /** put still picture k on screen; false when its file is not here or a newer seek/play/pause took over */
  async function loadPhoto(k: number, run: number): Promise<boolean> {
    const c = project!.clips[k];
    lastGone = -1;
    const url = await addressOf(c.media);
    if (!url) { lastGone = k; return false; }
    if (run !== loadRun) return false;
    if (photo.src !== url) {
      photo.hidden = true; photo.style.transform = '';   // never show the new picture at the old one's zoom
      photo.src = url;
      try { await photo.decode(); } catch { hooks.onProblem(`แสดงรูปของช่องที่ ${k + 1} ไม่ได้`); }
      if (run !== loadRun) return false;
    }
    return true;
  }
  const zoomPhoto = (k: number, u: number) => {
    const c = project?.clips[k];
    if (c) photo.style.transform = `scale(${photoZoom(u, clipLength(c))})`;
  };

  /** move an element to `at`; never waits more than 3 s (a file that never answers must not stop the screen) */
  function seekEl(el: HTMLMediaElement, at: number): Promise<void> {
    return new Promise(res => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('seeked', finish); el.removeEventListener('loadedmetadata', go); el.removeEventListener('error', finish);
        clearTimeout(limit);
        res();
      };
      function go() {
        el.removeEventListener('loadedmetadata', go);
        if (Math.abs(el.currentTime - at) < 0.02) { finish(); return; }
        el.addEventListener('seeked', finish);
        el.currentTime = at;
      }
      const limit = setTimeout(finish, 3000);
      el.addEventListener('error', finish);
      if (el.readyState >= 1) go(); else el.addEventListener('loadedmetadata', go);
    });
  }

  const gone = (k: number) => `คลิปที่ ${k + 1} (${project!.clips[k].name}) ไม่อยู่ในเครื่องนี้ จึงเล่นตัวอย่างต่อไม่ได้ ใส่คลิปกลับจากแถบแดงด้านบน`;

  /** the clip's and the music's volume at the current time, with the fades (V2) */
  function applyLevels() {
    if (!project) return;
    const l = hooks.levels?.(t) ?? { clip: 1, music: 1 };
    const c = project.clips[shownClip];
    if (c) setVolume(els[front], c.kind === 'video' && c.hasAudio && mode === 'video' ? c.volume * l.clip : 0);
    if (project.music) setVolume(music, project.music.volume * l.music);
    syncVoice();
  }

  /** the voice of the scene at `t` plays while the preview plays, within 0.3 s of the timeline */
  function syncVoice() {
    if (!project) return;
    const span = playing ? voiceSpans(project).find(v => t >= v.start && t < v.end - 0.02) : undefined;
    if (!span) { if (!voice.paused) voice.pause(); return; }
    const src = `/api/voice/${span.audio}/file`, at = t - span.start;
    if (voice.getAttribute('src') !== src) { voice.setAttribute('src', src); voice.load(); }
    if (voice.readyState >= 1 && Math.abs(voice.currentTime - at) > 0.3) voice.currentTime = at;
    setVolume(voice, hooks.voiceLevel?.(t) ?? 1);
    if (voice.paused && voice.readyState >= 1) voice.play().catch(e => { if (playing && voiceProblem !== src) { voiceProblem = src; failed('เล่นเสียงพากย์ไม่ได้', e); } });
  }

  /** show timeline time `to` (paused); returns false when a newer seek/play/pause took over or the clip is not here */
  async function seek(to: number): Promise<boolean> {
    if (!project) return false;
    const run = ++loadRun;
    t = Math.max(0, Math.min(to, totalLength(project)));
    const at = clipAt(project, t);
    hooks.onTime(t);
    if (!at) { els.forEach(e => { e.removeAttribute('src'); e.load(); }); photo.removeAttribute('src'); show('none'); shownClip = -1; return false; }
    const c = project.clips[at.index];
    const u = t - clipStarts(project)[at.index];
    if (isEmptySlot(c)) {
      els[front].pause(); show('none'); shownClip = at.index;
    } else if (c.kind === 'photo') {
      els[front].pause();
      if (!(await loadPhoto(at.index, run))) return false;
      zoomPhoto(at.index, u); show('photo'); shownClip = at.index;
    } else if (at.index !== shownClip || !photo.hidden || els[front].hidden) {
      const back = els[1 - front];
      if (!(await load(back, at.index, Math.min(at.local, c.out - 0.03), run))) return false;
      els[front].pause();
      front = 1 - front; show('video'); shownClip = at.index;
    } else {
      await seekEl(els[front], Math.min(at.local, c.out - 0.03));
      if (run !== loadRun) return false;
    }
    if (project.music) {
      const url = await addressOf(project.music.media);
      if (run !== loadRun) return false;
      if (url) {
        if (music.src !== url) music.src = url;
        await seekEl(music, Math.min(t, project.music.dur));
        if (run !== loadRun) return false;
      }
    }
    applyLevels();
    return true;
  }

  /** keep time on the clock from timeline time `from` (still picture, empty slot, held last picture) */
  function clock(from: number) { mode = 'clock'; clockBase = from; clockFrom = performance.now(); }

  function tick() {
    if (!playing || !project) return;
    const k = shownClip;
    const c = project.clips[k];
    if (!c) { pause(); return; }
    const start = clipStarts(project)[k], len = clipLength(c);
    if (mode === 'video') {
      const v = els[front];
      t = start + (v.currentTime - c.in);
      if (v.currentTime >= c.out - 0.02 || v.ended) {
        if (c.hold > 0.01 && t < start + len - 0.02) { v.pause(); clock(start + (c.out - c.in)); }   // hold the last picture
        else { advance(k); return; }
      }
    } else {
      t = clockBase + (performance.now() - clockFrom) / 1000;
      if (c.kind === 'photo' && !isEmptySlot(c)) zoomPhoto(k, t - start);
      if (t >= start + len) { advance(k); return; }
    }
    applyLevels();
    hooks.onTime(t);
    raf = requestAnimationFrame(tick);
  }

  function advance(k: number) {
    if (!project) return;
    if (k + 1 >= project.clips.length) { pause(); void seek(totalLength(project)); return; }
    void next(k + 1);
  }

  const failed = (what: string, e: unknown) => hooks.onProblem(`${what}: ${e instanceof Error ? e.message : String(e)}`);

  /** start clip k at its beginning (a video is preloaded in the hidden element when possible) */
  async function next(k: number) {
    const c = project!.clips[k];
    const run = ++loadRun;
    const start = clipStarts(project!)[k];
    if (isEmptySlot(c) || c.kind === 'photo') {
      if (!isEmptySlot(c)) {
        const ok = await loadPhoto(k, run);
        if (run !== loadRun || !playing) return;
        if (!ok) { pause(); hooks.onProblem(gone(k)); return; }
        zoomPhoto(k, 0);
      }
      els[front].pause();
      show(isEmptySlot(c) ? 'none' : 'photo'); shownClip = k;
      clock(start);
      applyLevels();
      preload(k + 1);
      raf = requestAnimationFrame(tick);
      return;
    }
    const back = els[1 - front];
    const ok = await load(back, k, c.in, run);
    if (run !== loadRun || !playing) return;          // paused (or moved) while the next clip was loading
    if (!ok) { pause(); hooks.onProblem(gone(k)); return; }
    els[front].pause();
    front = 1 - front; show('video'); shownClip = k; mode = 'video';
    applyLevels();
    try { await els[front].play(); } catch (e) { pause(); failed('เล่นตัวอย่างไม่ได้', e); return; }
    if (run !== loadRun || !playing) { els[front].pause(); return; }
    preload(k + 1);
    raf = requestAnimationFrame(tick);
  }

  /** load the video clip after k into the hidden element so it is ready */
  function preload(k: number) {
    if (!project || k >= project.clips.length) return;
    const c = project.clips[k];
    if (isEmptySlot(c) || c.kind !== 'video') return;
    void addressOf(c.media).then(url => {
      const back = els[1 - front];
      if (url && back.src !== url && playing) { back.src = url; back.currentTime = c.in; }
    });
  }

  async function play() {
    if (!project || !project.clips.length || playing || starting) return;
    starting = true;
    try {
      audio();
      void ctx?.resume();
      const run0 = loadRun + 1;   // the run seek() is about to start; pause() during it bumps loadRun past this
      const ready = await seek(t >= totalLength(project) - 0.05 ? 0 : t);
      if (loadRun !== run0) return;
      const at = clipAt(project, t);
      if (!at) return;
      if (!ready) { if (lastGone === at.index && shownClip !== at.index) hooks.onProblem(gone(at.index)); return; }
      const run = loadRun;
      playing = true; hooks.onPlayState(true);
      const c = project.clips[at.index];
      const start = clipStarts(project)[at.index];
      if (!isEmptySlot(c) && c.kind === 'video' && t < start + (c.out - c.in) - 0.02) {
        mode = 'video';
        applyLevels();
        try { await els[front].play(); }
        catch (e) { pause(); failed('เล่นตัวอย่างไม่ได้', e); return; }
        if (run !== loadRun || !playing) { els[front].pause(); return; }
      } else clock(t);
      if (project.music && t < project.music.dur && music.src) {
        music.play().catch(e => { if (playing) failed('เล่นเพลงประกอบไม่ได้', e); });
      }
      preload(at.index + 1);
      raf = requestAnimationFrame(tick);
    } finally { starting = false; }
  }

  function pause() {
    loadRun++;                    // a clip change or start still waiting for its file stops here
    playing = false;
    cancelAnimationFrame(raf);
    els.forEach(e => e.pause());
    music.pause();
    voice.pause();
    mode = 'video';
    hooks.onPlayState(false);
  }

  return {
    /** a new or changed project (after an edit, undo, or opening another one); stays at the same time */
    async setProject(p: VideoProject | null) {
      const wasPlaying = playing;
      pause();
      project = p;
      shownClip = -1;
      if (p) await seek(Math.min(t, totalLength(p)));
      if (wasPlaying && p) void play();
    },
    seek, play, pause,
    get playing() { return playing; },
    /** play() is still getting the first clip ready (a second tap means stop) */
    get starting() { return starting; },
    get time() { return t; },
    /** a clip's file came back (e.g. after choosing the original again) */
    reload() { shownClip = -1; return seek(t); },
    /** the picture on screen now (for the still frame behind the text editor), or null when there is none */
    picture(): Picture | null {
      if (!photo.hidden && photo.naturalWidth > 0) {
        const z = /scale\(([\d.]+)\)/.exec(photo.style.transform);
        return { source: photo, w: photo.naturalWidth, h: photo.naturalHeight, zoom: z ? Number(z[1]) : 1 };
      }
      const v = els[front];
      return shownClip >= 0 && !v.hidden && v.readyState >= 2 && v.videoWidth > 0 ? { source: v, w: v.videoWidth, h: v.videoHeight, zoom: 1 } : null;
    }
  };
}

export type Player = ReturnType<typeof createPlayer>;
